/**
 * HP Header
 * Controller for sections/hp-header.liquid.
 *
 *   <hp-header>   scroll state, menu open/close, menu panels
 *   search modal  #search-modal-root, opened by [data-hph-search-open]
 *
 * State classes (styled in hp-header.css)
 *   hp-header.is-scrolled     page has moved away from the top
 *   hp-header.is-menu-open    menu is open
 *   .hph-menu__panel.is-active
 *   .hph-backdrop.is-visible
 *   body.hph-lock             page scroll locked while the menu is open (see hp-header.css)
 */
(function () {
  'use strict';

  if (customElements.get('hp-header')) return;

  var DESKTOP = '(min-width: 900px)';
  var SCROLL_THRESHOLD = 20;

  class HpHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-hph-bar]');
      this.menu = this.querySelector('[data-hph-menu]');
      this.toggle = this.querySelector('[data-hph-toggle]');
      this.backdrop = (this.parentElement || document).querySelector('[data-hph-backdrop]');
      this.cartBadge = this.querySelector('[data-hph-cart-count]');
      this.desktop = window.matchMedia(DESKTOP);
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.measure = this.measure.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.measure);
      document.addEventListener('keydown', this.onKeydown);
      this.desktop.addEventListener('change', this.onLayoutChange);
      this.addEventListener('transitionend', this.measure);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this);
      }

      if (this.toggle && this.menu) {
        this.toggle.addEventListener('click', () => this.setMenu(!this.isOpen));
        this.menu.addEventListener('click', (event) => this.onMenuClick(event));
        this.menu.addEventListener('mouseover', (event) => this.onMenuHover(event));
        this.menu.addEventListener('mouseleave', () => this.onMenuLeave());
      }
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.setMenu(false));
      }

      // Opening any side drawer (cart, samples, account) closes the menu first.
      this.addEventListener('click', (event) => {
        if (this.isOpen && event.target.closest('side-drawer-opener, [data-hph-search-open]')) {
          this.setMenu(false);
        }
      });

      this.watchCartBadge();
      this.updateScrolled();
      this.measure();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.measure);
      document.removeEventListener('keydown', this.onKeydown);
      this.desktop.removeEventListener('change', this.onLayoutChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.cartObserver) this.cartObserver.disconnect();
      document.body.classList.remove('hph-lock');
    }

    get isOpen() {
      return this.classList.contains('is-menu-open');
    }

    /* ── Scroll + measurements ─────────────────────────────────────────── */

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateScrolled();
      });
    }

    updateScrolled() {
      this.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
    }

    /**
     * --hph-menu-offset  distance from the top of the viewport to the bottom
     *                    of the bar; the menu fills what is left below it.
     * --header-h         bar height, read by assets/pdp-split-scroll.js.
     */
    measure() {
      var rect = this.getBoundingClientRect();
      this.style.setProperty('--hph-menu-offset', Math.max(0, Math.round(rect.bottom)) + 'px');
      document.documentElement.style.setProperty('--header-h', Math.round(rect.height) + 'px');
    }

    /* ── Menu open / close ─────────────────────────────────────────────── */

    setMenu(open) {
      if (!this.menu || open === this.isOpen) return;

      this.classList.toggle('is-menu-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('hph-lock', open);
      if (this.backdrop) this.backdrop.classList.toggle('is-visible', open);

      if (open) {
        this.measure();
      } else {
        this.hovered = null;
        this.closePanels(1);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      var modal = document.getElementById('search-modal-root');
      if (modal && modal.hasAttribute('data-open')) return;

      // Below 900px Escape steps back one panel before it closes the menu.
      var active = this.menu.querySelectorAll('.hph-menu__panel--sub.is-active');
      if (!this.desktop.matches && active.length) {
        this.closePanel(active[active.length - 1], true);
        return;
      }
      this.setMenu(false);
      this.toggle.focus();
    }

    onLayoutChange() {
      // Columns and sliding panels do not share state; start clean.
      this.closePanels(1);
      this.measure();
    }

    /* ── Panels ────────────────────────────────────────────────────────── */

    triggerFor(panel) {
      return this.menu.querySelector('[data-hph-target="' + panel.id + '"]');
    }

    openPanel(trigger) {
      var panel = document.getElementById(trigger.getAttribute('data-hph-target'));
      if (!panel || panel.classList.contains('is-active')) return panel;

      // Close whatever is open at this level or deeper, then show the new one.
      this.closePanels(Number(panel.dataset.level) - 1);
      panel.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      return panel;
    }

    closePanel(panel, restoreFocus) {
      panel.querySelectorAll('.hph-menu__panel.is-active').forEach((child) => this.closePanel(child));
      panel.classList.remove('is-active');
      var trigger = this.triggerFor(panel);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) trigger.focus({ preventScroll: true });
      }
    }

    /** Close every open panel deeper than `level` (1 = keep only the root). */
    closePanels(level) {
      if (!this.menu) return;
      this.menu.querySelectorAll('.hph-menu__panel--sub.is-active').forEach((panel) => {
        if (Number(panel.dataset.level) > level) this.closePanel(panel);
      });
    }

    onMenuClick(event) {
      var back = event.target.closest('[data-hph-back]');
      if (back) {
        this.closePanel(back.closest('[data-hph-panel]'), true);
        return;
      }

      var trigger = event.target.closest('[data-hph-target]');
      if (!trigger) return;

      var panel = document.getElementById(trigger.getAttribute('data-hph-target'));
      var isOpen = panel && panel.classList.contains('is-active');
      var href = trigger.getAttribute('href');
      var hasUrl = href && href !== '#';

      // From 900px a parent whose column is already showing (hover, or a
      // first tap on a touch screen) behaves as a normal link.
      if (this.desktop.matches && isOpen && hasUrl) return;

      event.preventDefault();
      if (isOpen) return;

      this.openPanel(trigger);
      if (!this.desktop.matches && panel) {
        var first = panel.querySelector('[data-hph-back]');
        if (first) first.focus({ preventScroll: true });
      }
    }

    /**
     * From 900px the columns follow the pointer, as in the old header:
     *   - pointing at a parent link opens its column and closes its siblings'
     *   - pointing at a plain link closes anything deeper than its column
     *   - pointing at empty space in a column (below the list, or the
     *     banner) closes the columns to the right of it
     *   - leaving the menu altogether collapses it back to level 1
     */
    onMenuHover(event) {
      if (!this.desktop.matches) return;
      var link = event.target.closest('.hph-menu__link');

      if (link) {
        if (link === this.hovered) return;
        this.hovered = link;
        if (link.hasAttribute('data-hph-target')) {
          this.openPanel(link);
        } else {
          this.closePanels(Number(link.closest('[data-hph-panel]').dataset.level));
        }
        return;
      }

      this.hovered = null;
      var panel = event.target.closest('[data-hph-panel]');
      if (!panel || event.target.closest('.hph-menu__item')) return;
      var level = Number(panel.dataset.level);
      if (level < 3) this.closePanels(level);
    }

    onMenuLeave() {
      if (!this.desktop.matches) return;
      this.hovered = null;
      this.closePanels(1);
    }

    /* ── Cart badge ────────────────────────────────────────────────────── */

    /**
     * cart.js and fabric-samples.js rewrite the inside of .cart-icon-bubble
     * after every cart change. Hide the badge whenever the count reads 0.
     */
    watchCartBadge() {
      if (!this.cartBadge) return;
      var sync = () => {
        var count = parseInt(this.cartBadge.textContent, 10);
        var hasMarkup = this.cartBadge.childElementCount > 0;
        // No markup at all means 100+ items: keep the dot visible.
        this.cartBadge.classList.toggle('is-empty', hasMarkup && !(count > 0));
      };
      this.cartObserver = new MutationObserver(sync);
      this.cartObserver.observe(this.cartBadge, { childList: true, subtree: true, characterData: true });
    }
  }

  customElements.define('hp-header', HpHeader);

  /* ────────────────────────────────────────────────────────────────────────
     Search modal
     Markup: snippets/hp-header-search-modal.liquid
     Results are filled in by assets/predictive-search.js, which also reads
     modal._defaultSidebarHTML / _defaultProductsHTML to restore the popular
     view when the field is cleared.
     ──────────────────────────────────────────────────────────────────────── */
  function initSearchModal() {
    var modal = document.getElementById('search-modal-root');
    if (!modal || modal.hphReady) return;
    modal.hphReady = true;

    var input = modal.querySelector('#search-modal-input');
    var clearButton = modal.querySelector('.search-modal__clear');
    var sidebar = modal.querySelector('[data-search-sidebar]');
    var products = modal.querySelector('[data-search-products]');
    var lastTrigger = null;
    var prewarmed = false;

    modal._defaultSidebarHTML = sidebar ? sidebar.innerHTML : '';
    modal._defaultProductsHTML = products ? products.innerHTML : '';

    // Warm Shopify's section cache so the first keystroke returns quickly.
    function prewarm() {
      if (prewarmed) return;
      prewarmed = true;
      if ((navigator.connection && navigator.connection.saveData) || navigator.onLine === false) return;

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      [
        root + 'search?q=a&type=product&options[prefix]=last&section_id=predictive-search-products',
        root + 'search/suggest?q=a&resources[type]=query,collection,page,article&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search'
      ].forEach(function (url) {
        fetch(url, { credentials: 'same-origin', priority: 'low' }).catch(function () {});
      });
    }

    function syncClearButton() {
      if (!input || !clearButton) return;
      var empty = input.value.trim() === '';
      clearButton.hidden = empty;
      clearButton.classList.toggle('hidden', empty);
    }

    function open(trigger) {
      lastTrigger = trigger || null;
      modal.setAttribute('data-open', '');
      document.body.classList.add('search-modal-open');
      setTimeout(function () {
        if (input) input.focus();
      }, 50);
      (window.requestIdleCallback || function (cb) { setTimeout(cb, 300); })(prewarm, { timeout: 1000 });
    }

    function close() {
      modal.removeAttribute('data-open');
      document.body.classList.remove('search-modal-open');
      if (lastTrigger) lastTrigger.focus({ preventScroll: true });
    }

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-hph-search-open]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });

    modal.addEventListener('click', function (event) {
      if (event.target.closest('[data-search-modal-close]')) {
        event.preventDefault();
        close();
        return;
      }

      // Popular search pill: put the term in the field and run the search.
      var pill = event.target.closest('[data-search-query]');
      if (pill && input) {
        input.value = pill.getAttribute('data-search-query') || pill.textContent.trim();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.hasAttribute('data-open')) close();
    });

    if (input) {
      input.addEventListener('input', syncClearButton);
      syncClearButton();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchModal);
  } else {
    initSearchModal();
  }
  // Theme editor re-renders the section; hook the fresh modal up again.
  document.addEventListener('shopify:section:load', initSearchModal);
})();
